import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from 'react';
import { Client } from '../types';

interface AuthContextType {
    isAuthenticated: boolean;
    client: Client | null;
    token: string | null;
    login: (token: string, client: Client) => void;
    logout: () => void;
    updateClient: (data: Partial<Client>) => void;
    loading: boolean;
    profileCompleted: boolean;   // ✅ اضافه شد
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

// ✅ تشخیص اینکه این client قدیمی (قبل از نسخه جدید پروفایل) هست یا نه
const isLegacyClient = (client: any): boolean => {
    if (!client) return false;

    // اگر این فلگ‌های جدید وجود داشته باشن یعنی کاربر نسخه جدید هست
    const hasV2Flags =
        'profile_confirmed_at' in client ||
        'must_update_profile' in client ||
        'must_complete_profile' in client;

    // کاربرهایی که قبل از این تغییر ساخته شدن این‌ها رو ندارن
    return !hasV2Flags;
};

// ✅ محاسبهٔ کامل بودن پروفایل
const computeProfileCompleted = (client: Client | null): boolean => {
    if (!client) return false;

    const anyClient: any = client;

    // اگر بک‌اند مستقیماً فلگ داده
    if (anyClient.is_profile_confirmed === true) return true;
    if (anyClient.must_update_profile === false) return true;
    if (anyClient.must_complete_profile === false) return true;

    // fallback: چک کردن آدرس و لوکیشن
    const hasLocation =
        !!anyClient.country_id &&
        !!anyClient.province_id &&
        !!anyClient.city_id &&
        !!anyClient.address;

    return hasLocation;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [client, setClient] = useState<Client | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [profileCompleted, setProfileCompleted] = useState(false);

    useEffect(() => {
        const storedToken = localStorage.getItem('auth_token');
        const storedClient = localStorage.getItem('client_data');

        if (storedToken && storedClient) {
            try {
                const parsedClient: Client = JSON.parse(storedClient);

                // ✅ اگر کاربر «قدیمی» است → همه‌چیز پاک شود و مجبور به لاگین مجدد شود
                if (isLegacyClient(parsedClient)) {
                    console.warn(
                        '[Auth] legacy client detected → clearing storage and forcing re-login'
                    );
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('client_data');
                    setIsAuthenticated(false);
                    setClient(null);
                    setToken(null);
                    setProfileCompleted(false);
                } else {
                    // ✅ کاربر نرمال (نسخه جدید)
                    setToken(storedToken);
                    setClient(parsedClient);
                    setIsAuthenticated(true);
                    setProfileCompleted(computeProfileCompleted(parsedClient));
                }
            } catch (e) {
                console.error('Failed to parse client_data from localStorage', e);
                localStorage.removeItem('auth_token');
                localStorage.removeItem('client_data');
            }
        }

        setLoading(false);
    }, []);

    const login = (newToken: string, clientData: Client) => {
        localStorage.setItem('auth_token', newToken);
        localStorage.setItem('client_data', JSON.stringify(clientData));
        setToken(newToken);
        setClient(clientData);
        setIsAuthenticated(true);
        setProfileCompleted(computeProfileCompleted(clientData));
    };

    const updateClient = (data: Partial<Client>) => {
        setClient(prev => {
            if (!prev) return prev;
            const merged = { ...prev, ...data };
            localStorage.setItem('client_data', JSON.stringify(merged));
            setProfileCompleted(computeProfileCompleted(merged as Client));
            return merged as Client;
        });
    };

    const logout = () => {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('client_data');
        setToken(null);
        setClient(null);
        setIsAuthenticated(false);
        setProfileCompleted(false);
    };

    const value: AuthContextType = {
        isAuthenticated,
        client,
        token,
        login,
        logout,
        updateClient,
        loading,
        profileCompleted, // ✅ حتماً اینجا هم برگردون
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
